import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { InvitationService } from '../../services/invitation.service';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Component({
  selector: 'app-invitation-create',
  templateUrl: './invitation-create.component.html',
  styleUrls: ['./invitation-create.component.sass'],
  standalone: false
})
export class InvitationCreateComponent implements OnInit {
  invitationForm: FormGroup;
  condominiumId: number;
  isLoading = false;
  maxEmails = 20;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar,
    private invitationService: InvitationService
  ) {
    this.invitationForm = this.fb.group({
      emails: this.fb.array([this.createEmailFormControl()])
    });
  }

  ngOnInit(): void {
    this.condominiumId = +this.route.parent!.snapshot.paramMap.get('id')!;
  }

  get emailsFormArray(): FormArray {
    return this.invitationForm.get('emails') as FormArray;
  }

  get canAddMoreEmails(): boolean {
    return this.emailsFormArray.length < this.maxEmails;
  }

  createEmailFormControl(): FormGroup {
    return this.fb.group({
      email: ['', [Validators.required, Validators.email]]
    });
  }

  addEmailField(): void {
    if (this.canAddMoreEmails) {
      this.emailsFormArray.push(this.createEmailFormControl());
    }
  }

  removeEmailField(index: number): void {
    if (this.emailsFormArray.length > 1) {
      this.emailsFormArray.removeAt(index);
    }
  }

  onCancel(): void {
    this.router.navigate(['/condominium', this.condominiumId, 'invitations']);
  }

  onBack(): void {
    this.router.navigate(['/condominium', this.condominiumId, 'invitations']);
  }

  onSubmit(): void {
    if (this.invitationForm.valid) {
      this.isLoading = true;
      
      const emails = this.emailsFormArray.value
        .map((control: any) => control.email)
        .filter((email: string) => email.trim() !== '');

      const invitationRequests = emails.map((email: string) =>
        this.invitationService.createInvitation(this.condominiumId, email).pipe(
          catchError(error => of({ error: true, email, details: error }))
        )
      );

      forkJoin(invitationRequests).subscribe(results => {
        this.isLoading = false;
        
        const successCount = results.filter(result => !(result as any).error).length;
        const errorCount = results.filter(result => (result as any).error).length;

        if (errorCount === 0) {
          // Todos os convites foram criados com sucesso
          this.snackBar.open(
            `${successCount} convite(s) criado(s) com sucesso!`,
            'Fechar',
            { duration: 5000, panelClass: ['success-snackbar'] }
          );
          this.router.navigate(['/condominium', this.condominiumId, 'invitations']);
        } else if (successCount === 0) {
          // Todos os convites falharam
          this.snackBar.open(
            'Erro ao criar os convites. Tente novamente.',
            'Fechar',
            { duration: 5000, panelClass: ['error-snackbar'] }
          );
        } else {
          // Alguns convites foram criados, outros falharam
          this.snackBar.open(
            `${successCount} convite(s) criado(s), mas ${errorCount} não foram criados.`,
            'Fechar',
            { duration: 5000, panelClass: ['warning-snackbar'] }
          );
          this.router.navigate(['/condominium', this.condominiumId, 'invitations']);
        }
      });
    } else {
      this.snackBar.open('Por favor, corrija os erros no formulário.', 'Fechar', {
        duration: 3000,
        panelClass: ['error-snackbar']
      });
    }
  }
}
